import React, { useState, useEffect } from 'react';
import { AlertTriangle, Check, Clock, Smartphone, Code, XCircle, RefreshCw, X } from 'lucide-react';
import axios from 'axios';

interface CrashReport {
  id: number;
  user_id: number | null;
  crash_type: string;
  error_message: string | null;
  stack_trace: string | null;
  ios_version: string | null;
  app_version: string | null;
  device_model: string | null;
  is_resolved: boolean;
  created_at: string;
}

interface CrashStats {
  total_crashes: number;
  unresolved_crashes: number;
  crash_rate: number;
  most_common_crashes: { type: string; count: number }[];
  crashes_by_ios: { version: string; count: number }[];
}

const CrashReports: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [crashes, setCrashes] = useState<CrashReport[]>([]);
  const [stats, setStats] = useState<CrashStats | null>(null);
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);
  const [selectedCrash, setSelectedCrash] = useState<CrashReport | null>(null);

  useEffect(() => { fetchData(); }, [unresolvedOnly]);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const [crashesRes, statsRes] = await Promise.all([
        axios.get(`https://api.lifeshare.app/api/v1/admin/crash-reports?unresolved_only=${unresolvedOnly}`),
        axios.get('https://api.lifeshare.app/api/v1/admin/crash-reports/stats?days=7')
      ]);
      setCrashes(crashesRes.data.crashes || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const markAsResolved = async (crashId: number) => {
    try {
      await axios.put(`https://api.lifeshare.app/api/v1/admin/crash-reports/${crashId}/resolve`, {});
      fetchData();
      setSelectedCrash(null);
    } catch (error) {
      console.error('Resolve error:', error);
    }
  };

  const getSeverityColor = (crashType: string) => {
    if (crashType.includes('Fatal') || crashType.includes('Crash')) return 'bg-red-100 text-red-700';
    if (crashType.includes('Error')) return 'bg-orange-100 text-orange-700';
    return 'bg-yellow-100 text-yellow-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="max-w-6xl mx-auto space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900">Çökme Raporları</h1>
            <p className="text-sm text-gray-500 mt-1">Uygulama çökmelerini izle ve çöz</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setUnresolvedOnly(!unresolvedOnly)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${unresolvedOnly ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700'}`}>
              {unresolvedOnly ? 'Sadece Açık' : 'Tümü'}
            </button>
            <button onClick={fetchData} disabled={refreshing} className="p-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors">
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-1 opacity-70"><XCircle className="w-4 h-4" /></div>
              <p className="text-2xl font-bold text-red-700">{stats.total_crashes}</p>
              <p className="text-xs text-red-600">Toplam (7g)</p>
            </div>
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-1 opacity-70"><Clock className="w-4 h-4" /></div>
              <p className="text-2xl font-bold text-orange-700">{stats.unresolved_crashes}</p>
              <p className="text-xs text-orange-600">Çözülmemiş</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-1 opacity-70"><AlertTriangle className="w-4 h-4" /></div>
              <p className="text-2xl font-bold text-purple-700">{stats.crash_rate}%</p>
              <p className="text-xs text-purple-600">Çökme Oranı</p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-2xl p-4">
              <div className="flex items-center gap-2 mb-1 opacity-70"><Code className="w-4 h-4" /></div>
              <p className="text-sm font-bold text-blue-700 truncate">{stats.most_common_crashes[0]?.type || 'Yok'}</p>
              <p className="text-xs text-blue-600">{stats.most_common_crashes[0]?.count || 0} adet</p>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-2xl overflow-hidden">
          {crashes.length === 0 ? (
            <div className="p-12 text-center">
              <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="font-medium text-gray-900">Çökme bulunamadı</p>
              <p className="text-sm text-gray-500 mt-1">Her şey yolunda! 🎉</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Tür</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cihaz</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">iOS</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Uygulama</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Zaman</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Durum</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">İşlem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {crashes.slice(0, 50).map((crash) => (
                      <tr key={crash.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 rounded-xl text-xs font-medium ${getSeverityColor(crash.crash_type)}`}>{crash.crash_type}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{crash.device_model || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{crash.ios_version || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{crash.app_version || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{new Date(crash.created_at).toLocaleDateString('tr-TR')}</td>
                        <td className="px-4 py-3">
                          {crash.is_resolved ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-xl text-xs"><Check className="w-3 h-3" />Çözüldü</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-xl text-xs"><Clock className="w-3 h-3" />Açık</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => setSelectedCrash(crash)} className="text-sm text-purple-600 hover:text-purple-800 font-medium">Detay</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-gray-100">
                {crashes.slice(0, 50).map((crash) => (
                  <div key={crash.id} onClick={() => setSelectedCrash(crash)} className="p-4 hover:bg-gray-50 cursor-pointer">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span className={`inline-flex px-2 py-1 rounded-xl text-xs font-medium ${getSeverityColor(crash.crash_type)}`}>{crash.crash_type}</span>
                      {crash.is_resolved ? (
                        <span className="text-xs text-green-600 flex items-center gap-1"><Check className="w-3 h-3" />Çözüldü</span>
                      ) : (
                        <span className="text-xs text-red-600">Açık</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Smartphone className="w-3 h-3" />{crash.device_model || '-'}</span>
                      <span>iOS {crash.ios_version || '-'}</span>
                      <span>v{crash.app_version || '-'}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2">{new Date(crash.created_at).toLocaleString('tr-TR')}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Crash Detail Modal */}
        {selectedCrash && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => setSelectedCrash(null)}>
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b border-gray-200 flex items-start justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Crash Details</h3>
                  <p className="text-sm text-gray-500 mt-1">ID: {selectedCrash.id}</p>
                </div>
                <button onClick={() => setSelectedCrash(null)} className="p-2 hover:bg-gray-100 rounded-xl"><X className="w-5 h-5 text-gray-400" /></button>
              </div>
              <div className="p-6 space-y-6">
                <div>
                  <label className="text-sm font-semibold text-gray-700">Crash Type</label>
                  <p className="mt-1 text-gray-900">{selectedCrash.crash_type}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Error Message</label>
                  <p className="mt-1 text-gray-900 bg-gray-50 p-3 rounded-xl font-mono text-sm">{selectedCrash.error_message || 'No error message'}</p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-700">Stack Trace</label>
                  <pre className="mt-1 text-xs text-gray-900 bg-gray-50 p-3 rounded-xl overflow-x-auto max-h-64">{selectedCrash.stack_trace || 'No stack trace available'}</pre>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><label className="text-sm font-semibold text-gray-700">iOS Version</label><p className="mt-1 text-gray-900">{selectedCrash.ios_version || '-'}</p></div>
                  <div><label className="text-sm font-semibold text-gray-700">App Version</label><p className="mt-1 text-gray-900">{selectedCrash.app_version || '-'}</p></div>
                  <div><label className="text-sm font-semibold text-gray-700">Device Model</label><p className="mt-1 text-gray-900">{selectedCrash.device_model || '-'}</p></div>
                  <div><label className="text-sm font-semibold text-gray-700">Occurred At</label><p className="mt-1 text-gray-900">{new Date(selectedCrash.created_at).toLocaleString()}</p></div>
                </div>
                {!selectedCrash.is_resolved && (
                  <div className="pt-4 border-t border-gray-200">
                    <button onClick={() => markAsResolved(selectedCrash.id)} className="w-full py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2">
                      <Check className="w-4 h-4" />Mark as Resolved
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CrashReports;
